import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Segment,
  Button,
  Menu,
  Table,
  Dimmer,
  Loader,
  Dropdown,
  Grid,
  Statistic,
  Checkbox,
} from 'semantic-ui-react';
import {
  getReadingMeetingList,
  getReportedMeetingSummary,
  getReportedMeetingList,
  getPopularMeetingList,
  approveMeeting,
  rejectMeeting,
  sendWarningToMeeting,
  suspendMeeting,
  updatePopularMeetingExposure,
} from '../api/MeetingManagementApi';
import {
  ReadingMeetingListDTO,
  ReportedMeetingSummaryDTO,
  ReportedMeetingListDTO,
  PopularMeetingListDTO,
} from '../dto/MeetingManagementDTOs';

const MeetingManagementPage: React.FC = () => {
  // TODO: 실제 인증 토큰을 localStorage나 context에서 가져오도록 수정
  const authToken = localStorage.getItem('accessToken') || '';

  // 날짜 포맷팅 함수 (YYYY-MM-DD)
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 내일 날짜 계산
  const getTomorrow = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return formatDate(tomorrow);
  };

  // N일 전 날짜 계산
  const getDaysAgo = (days: number): string => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return formatDate(date);
  };

  // 오늘 날짜 계산
  const getToday = (): string => {
    return formatDate(new Date());
  };

  // 초기 날짜 계산
  const getInitialStartDate = (): string => {
    return getToday();
  };

  const getInitialEndDate = (): string => {
    return getTomorrow();
  };

  // 독서모임 목록 관련 state
  const [readingMeetingLoading, setReadingMeetingLoading] = useState(true);
  const [readingMeetingData, setReadingMeetingData] = useState<ReadingMeetingListDTO | null>(null);
  const [readingMeetingStatusFilter, setReadingMeetingStatusFilter] = useState<string>('all');

  // 신고된 모임 관련 state
  const [reportedSummaryLoading, setReportedSummaryLoading] = useState(true);
  const [reportedSummaryData, setReportedSummaryData] = useState<ReportedMeetingSummaryDTO | null>(null);
  const [reportedMeetingLoading, setReportedMeetingLoading] = useState(true);
  const [reportedMeetingData, setReportedMeetingData] = useState<ReportedMeetingListDTO | null>(null);

  // 인기 모임 관련 state
  const [popularMeetingLoading, setPopularMeetingLoading] = useState(true);
  const [popularMeetingData, setPopularMeetingData] = useState<PopularMeetingListDTO | null>(null);

  // 필터 관련 state
  const [startDate, setStartDate] = useState(getInitialStartDate());
  const [endDate, setEndDate] = useState(getInitialEndDate());
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [dateInputEnabled, setDateInputEnabled] = useState(false);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string | undefined>('all');

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    
    if (period === 'today') {
      setDateInputEnabled(false);
      setStartDate(getToday());
      setEndDate(getTomorrow());
    } else if (period === '7days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(7));
      setEndDate(getTomorrow());
    } else if (period === '30days') {
      setDateInputEnabled(false);
      setStartDate(getDaysAgo(30));
      setEndDate(getTomorrow());
    } else if (period === 'all') {
      setDateInputEnabled(true);
    }
  };

  const ageGroupOptions = [
    { key: 'all', text: '전체', value: 'all' },
    { key: '10s', text: '10대', value: '10s' },
    { key: '20s30s', text: '20,30대', value: '20s30s' },
    { key: '40s50s', text: '40,50대', value: '40s50s' },
    { key: '60s', text: '60대이상', value: '60s' },
  ];

  // 독서모임 목록 로드
  const loadReadingMeetingList = async () => {
    setReadingMeetingLoading(true);
    try {
      const ageGroup = selectedAgeGroup === 'all' ? undefined : selectedAgeGroup;
      const status = readingMeetingStatusFilter === 'all' ? undefined : readingMeetingStatusFilter;
      const result = await getReadingMeetingList(
        authToken,
        startDate,
        endDate,
        ageGroup,
        status
      );
      if (result && result.errorCode === 0) {
        setReadingMeetingData(result.response);
      }
    } catch (error) {
      console.error('독서모임 목록 로드 실패:', error);
    } finally {
      setReadingMeetingLoading(false);
    }
  };

  // 신고된 모임 요약 로드
  const loadReportedMeetingSummary = async () => {
    setReportedSummaryLoading(true);
    try {
      const result = await getReportedMeetingSummary(authToken);
      if (result && result.errorCode === 0) {
        setReportedSummaryData(result.response);
      }
    } catch (error) {
      console.error('신고된 모임 요약 로드 실패:', error);
    } finally {
      setReportedSummaryLoading(false);
    }
  };

  // 신고된 모임 목록 로드
  const loadReportedMeetingList = async () => {
    setReportedMeetingLoading(true);
    try {
      const result = await getReportedMeetingList(authToken);
      if (result && result.errorCode === 0) {
        setReportedMeetingData(result.response);
      }
    } catch (error) {
      console.error('신고된 모임 목록 로드 실패:', error);
    } finally {
      setReportedMeetingLoading(false);
    }
  };

  // 인기 모임 목록 로드
  const loadPopularMeetingList = async () => {
    setPopularMeetingLoading(true);
    try {
      const result = await getPopularMeetingList(authToken);
      if (result && result.errorCode === 0) {
        setPopularMeetingData(result.response);
      }
    } catch (error) {
      console.error('인기 모임 목록 로드 실패:', error);
    } finally {
      setPopularMeetingLoading(false);
    }
  };

  useEffect(() => {
    loadReadingMeetingList();
  }, [startDate, endDate, selectedAgeGroup, readingMeetingStatusFilter]);

  useEffect(() => {
    loadReportedMeetingSummary();
    loadReportedMeetingList();
    loadPopularMeetingList();
  }, []);

  // 모임 승인
  const handleApprove = async (meetingNo: number) => {
    try {
      await approveMeeting(authToken, meetingNo);
      loadReadingMeetingList();
    } catch (error) {
      console.error('모임 승인 실패:', error);
    }
  };

  // 모임 반려
  const handleReject = async (meetingNo: number) => {
    try {
      await rejectMeeting(authToken, meetingNo);
      loadReadingMeetingList();
    } catch (error) {
      console.error('모임 반려 실패:', error);
    }
  };

  // 경고 발송
  const handleSendWarning = async (meetingNo: number) => {
    try {
      await sendWarningToMeeting(authToken, meetingNo);
      loadReportedMeetingSummary();
      loadReportedMeetingList();
    } catch (error) {
      console.error('경고 발송 실패:', error);
    }
  };

  // 모임 정지
  const handleSuspend = async (meetingNo: number) => {
    try {
      await suspendMeeting(authToken, meetingNo);
      loadReportedMeetingSummary();
      loadReportedMeetingList();
    } catch (error) {
      console.error('모임 정지 실패:', error);
    }
  };

  // 인기 모임 노출 설정 변경
  const handleToggleExposure = async (meetingNo: number, isExposed: boolean) => {
    try {
      await updatePopularMeetingExposure(authToken, meetingNo, isExposed);
      loadPopularMeetingList();
    } catch (error) {
      console.error('노출 설정 변경 실패:', error);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return '대기';
      case 'APPROVED':
        return '승인';
      case 'REJECTED':
        return '반려';
      case 'PROCESSING':
        return '처리중';
      case 'COMPLETED':
        return '완료';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'yellow';
      case 'APPROVED':
        return 'green';
      case 'REJECTED':
        return 'red';
      case 'PROCESSING':
        return 'blue';
      case 'COMPLETED':
        return 'green';
      default:
        return 'grey';
    }
  };

  return (
    <Container fluid>
      {/* 상단 제목 */}
      <Header as="h2" style={{ marginBottom: 16 }}>
        6. 모임 관리
      </Header>

      {/* 상단 필터 영역 */}
      <Segment
        raised
        style={{
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f8f9ff',
          marginBottom: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* 기간 선택 */}
          <Menu compact>
            <Menu.Item
              active={selectedPeriod === 'today'}
              onClick={() => handlePeriodChange('today')}
            >
              오늘
            </Menu.Item>
            <Menu.Item
              active={selectedPeriod === '7days'}
              onClick={() => handlePeriodChange('7days')}
            >
              7일
            </Menu.Item>
            <Menu.Item
              active={selectedPeriod === '30days'}
              onClick={() => handlePeriodChange('30days')}
            >
              30일
            </Menu.Item>
            <Menu.Item
              active={selectedPeriod === 'all'}
              onClick={() => handlePeriodChange('all')}
            >
              전체
            </Menu.Item>
          </Menu>

          {/* 연령대별 드롭다운 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 500 }}>연령대별:</span>
            <Dropdown
              selection
              options={ageGroupOptions}
              value={selectedAgeGroup}
              onChange={(e, { value }) => setSelectedAgeGroup(value as string | undefined)}
              style={{ minWidth: 120 }}
            />
          </div>
        </div>
      </Segment>

      {/* 1. 독서모임 전체 목록 (승인/반려) */}
      <Segment raised style={{ padding: '20px', marginBottom: 24 }}>
        <Header as="h4" style={{ marginBottom: 16, marginTop: 0 }}>
          독서모임 전체 목록 (승인/반려)
        </Header>

        {/* 서브 필터 */}
        <Menu compact style={{ marginBottom: 16 }}>
          <Menu.Item
            active={readingMeetingStatusFilter === 'all'}
            onClick={() => setReadingMeetingStatusFilter('all')}
          >
            전체
          </Menu.Item>
          <Menu.Item
            active={readingMeetingStatusFilter === 'PENDING'}
            onClick={() => setReadingMeetingStatusFilter('PENDING')}
          >
            승인 대기
          </Menu.Item>
          <Menu.Item
            active={readingMeetingStatusFilter === 'REJECTED'}
            onClick={() => setReadingMeetingStatusFilter('REJECTED')}
          >
            반려
          </Menu.Item>
        </Menu>

        {readingMeetingLoading ? (
          <Dimmer active inverted>
            <Loader size="small">로딩 중...</Loader>
          </Dimmer>
        ) : (
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>모임명</Table.HeaderCell>
                <Table.HeaderCell>모임장</Table.HeaderCell>
                <Table.HeaderCell>신청일</Table.HeaderCell>
                <Table.HeaderCell>상태</Table.HeaderCell>
                <Table.HeaderCell>관리</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {readingMeetingData?.meetings.map((meeting) => (
                <Table.Row key={meeting.meetingNo}>
                  <Table.Cell>{meeting.meetingName}</Table.Cell>
                  <Table.Cell>{meeting.hostName}</Table.Cell>
                  <Table.Cell>{meeting.applicationDate.replace(/-/g, '.')}</Table.Cell>
                  <Table.Cell>
                    <Button
                      size="small"
                      color={getStatusColor(meeting.status) as any}
                      style={{ minWidth: 60 }}
                    >
                      {getStatusLabel(meeting.status)}
                    </Button>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      size="small"
                      color="blue"
                      onClick={() => handleApprove(meeting.meetingNo)}
                      style={{ marginRight: 8 }}
                    >
                      승인
                    </Button>
                    <Button
                      size="small"
                      color="red"
                      onClick={() => handleReject(meeting.meetingNo)}
                    >
                      반려
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Segment>

      {/* 2. 신고된 모임 관리 (경고/정지) */}
      <Segment raised style={{ padding: '20px', marginBottom: 24 }}>
        <Header as="h4" style={{ marginBottom: 16, marginTop: 0 }}>
          신고된 모임 관리 (경고/정지)
        </Header>

        {/* 요약 박스 */}
        {reportedSummaryLoading ? (
          <Dimmer active inverted>
            <Loader size="small">로딩 중...</Loader>
          </Dimmer>
        ) : (
          <Grid columns={4} style={{ marginBottom: 24 }}>
            <Grid.Column>
              <Statistic>
                <Statistic.Value>{reportedSummaryData?.reportsReceived || 0}</Statistic.Value>
                <Statistic.Label>신고 접수</Statistic.Label>
              </Statistic>
            </Grid.Column>
            <Grid.Column>
              <Statistic>
                <Statistic.Value>{reportedSummaryData?.processingPending || 0}</Statistic.Value>
                <Statistic.Label>처리 대기</Statistic.Label>
              </Statistic>
            </Grid.Column>
            <Grid.Column>
              <Statistic>
                <Statistic.Value>{reportedSummaryData?.warningsSent || 0}</Statistic.Value>
                <Statistic.Label>경고 발송</Statistic.Label>
              </Statistic>
            </Grid.Column>
            <Grid.Column>
              <Statistic>
                <Statistic.Value>{reportedSummaryData?.forcedSuspension || 0}</Statistic.Value>
                <Statistic.Label>강세 징지</Statistic.Label>
              </Statistic>
            </Grid.Column>
          </Grid>
        )}

        {/* 신고된 모임 목록 테이블 */}
        {reportedMeetingLoading ? (
          <Dimmer active inverted>
            <Loader size="small">로딩 중...</Loader>
          </Dimmer>
        ) : (
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>모임명</Table.HeaderCell>
                <Table.HeaderCell>신고 사유</Table.HeaderCell>
                <Table.HeaderCell>누적 신고수</Table.HeaderCell>
                <Table.HeaderCell>상태</Table.HeaderCell>
                <Table.HeaderCell>관리</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {reportedMeetingData?.meetings.map((meeting) => (
                <Table.Row key={meeting.meetingNo}>
                  <Table.Cell>{meeting.meetingName}</Table.Cell>
                  <Table.Cell>{meeting.reportReason}</Table.Cell>
                  <Table.Cell>{meeting.cumulativeReportCount}</Table.Cell>
                  <Table.Cell>
                    <Button
                      size="small"
                      color={getStatusColor(meeting.status) as any}
                      style={{ minWidth: 60 }}
                    >
                      {getStatusLabel(meeting.status)}
                    </Button>
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      size="small"
                      color="red"
                      onClick={() => handleSendWarning(meeting.meetingNo)}
                      style={{ marginRight: 8 }}
                    >
                      경고
                    </Button>
                    <Button
                      size="small"
                      color="red"
                      onClick={() => handleSuspend(meeting.meetingNo)}
                    >
                      정지
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Segment>

      {/* 3. 인기 모임 설정 */}
      <Segment raised style={{ padding: '20px', marginBottom: 24 }}>
        <Header as="h4" style={{ marginBottom: 16, marginTop: 0 }}>
          인기 모임 설정
        </Header>

        {popularMeetingLoading ? (
          <Dimmer active inverted>
            <Loader size="small">로딩 중...</Loader>
          </Dimmer>
        ) : (
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>순위</Table.HeaderCell>
                <Table.HeaderCell>모임명</Table.HeaderCell>
                <Table.HeaderCell>모임장</Table.HeaderCell>
                <Table.HeaderCell>멤버수</Table.HeaderCell>
                <Table.HeaderCell>노출 실정</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {popularMeetingData?.meetings.map((meeting) => (
                <Table.Row key={meeting.meetingNo}>
                  <Table.Cell>{meeting.rank}</Table.Cell>
                  <Table.Cell>{meeting.meetingName}</Table.Cell>
                  <Table.Cell>{meeting.hostName}</Table.Cell>
                  <Table.Cell>{meeting.memberCount}</Table.Cell>
                  <Table.Cell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: meeting.isExposed ? '#0066cc' : '#999' }}>
                        {meeting.isExposed ? 'ON' : 'OFF'}
                      </span>
                      <Checkbox
                        toggle
                        checked={meeting.isExposed}
                        onChange={(e, { checked }) => {
                          if (checked !== undefined) {
                            handleToggleExposure(meeting.meetingNo, checked);
                          }
                        }}
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Segment>
    </Container>
  );
};

export default MeetingManagementPage;
